import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardContainer } from "../../../components/Card/CardContainer";
import Text from "../../../components/Text/Text";
import {
  Card,
  Col,
  ConfigProvider,
  Divider,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Table,
  Form,
  Alert,
  message,
  Checkbox,
} from "antd";
import Button from "../../../components/Button/Button";
import { useTabState } from "../../../hook/useTabState";
import {
  CalendarOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import { numberFormatter } from "../../../utility/Formatter";
import { color } from "../../../resource";
import SortFieldCustom from "../../../components/SortFieldTable/SortFieldCustom";
import { FlexCol } from "../../../components/Container/Container";
import Permission, { checkPermission } from "../../../components/Permission/Permission";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import Input from "../../../components/Input/Input";
import Select from "../../../components/Select/Select";
import {
  getCoDiscountEachShop,
  getCoSummaryEachShop,
  excelMutiCo,
  excelMutiCoAccounting,
} from "../../../datasource/CODiscountDatasource";
import { zoneDatasource } from "../../../datasource/ZoneDatasource";
import { useEffectOnce } from "react-use";
import DatePicker from "../../../components/DatePicker/DatePicker";
import dayjs from "dayjs";
import { useNotificationContext } from "../../../context/NotificationProvider";
import { useRecoilValue } from "recoil";
import { roleAtom } from "../../../store/RoleAtom";

export const IndexCoDiscountEachShop: React.FC = () => {
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const orderTabState = useTabState();
  const roleData = useRecoilValue(roleAtom);
  const isExcel = checkPermission(["coDiscountEachShop", "excel"], roleData);
  const navigate = useNavigate();
  const [page, setPage] = useState("1");
  const [searchParams, setSearchParams] = useSearchParams();
  const [zoneList, setZoneList] = useState<any>([]);
  const [data, setData] = useState<any>([]);
  const [shopSelected, setShopSelected] = useState<any[]>([]);
  const [dataSummary, setDataSummary] = useState<any>([]);
  const [searchText, setSearchText] = useState<any>(searchParams.get("searchText") || undefined);
  const [searchZone, setSearchZone] = useState<any>(searchParams.get("searchZone") || undefined);
  const [modalMutiSelect, setModalMutiSelect] = useState<boolean>(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const { startUploadProcess } = useNotificationContext();
  const [form] = Form.useForm();
  const startDateWatch = Form.useWatch("startDate", form);
  const endDateWatch = Form.useWatch("endDate", form);

  const fetchCoListEachShop = async () => {
    await getCoDiscountEachShop({
      company: profile?.company,
      page: page,
      take: 10,
      searchText,
      zone: searchZone,
      sortField: orderTabState.sortDirection ? orderTabState.sortField : "lastUpdatedAt",
      sortDirection: orderTabState.sortDirection,
    }).then((res) => {
      const mapData = res.data.map((d: any) => {
        const isChecked = shopSelected.find((p) => p.customerCompanyId === d.customerCompanyId);
        return {
          ...d,
          isChecked: isChecked ? true : false,
        };
      });

      setData({ ...res, data: mapData });
    });
  };
  const fetchZone = async () => {
    await zoneDatasource.getAllZoneByCompany(profile.company).then((res) => {
      setZoneList(res);
    });
  };
  const fetchCoSummaryEachShop = async () => {
    await getCoSummaryEachShop({
      company: profile?.company,
    }).then((res) => {
      setDataSummary(res);
    });
  };

  useEffectOnce(() => {
    fetchZone();
  });

  useEffect(() => {
    setLoading(true);
    fetchCoListEachShop();
    fetchCoSummaryEachShop();
    setTimeout(() => {
      setLoading(false);
    }, 300);
  }, [page, searchText, searchZone, orderTabState.sortField, orderTabState.sortDirection]);

  const PageTitle = () => {
    return (
      <Row justify={"space-between"} align={"middle"} gutter={8}>
        <Col>
          <div>
            <span
              className='card-label font-weight-bolder text-dark'
              style={{ fontSize: 22, fontWeight: "bold" }}
            >
              ส่วนลดรายร้าน
            </span>
          </div>
        </Col>
        <Col>
          <Col>
            <Button
              type='primary'
              typeButton={shopSelected.length ? "primary" : "disabled"}
              title={
                <>
                  <VerticalAlignBottomOutlined style={{ fontSize: "18px" }} /> Export (
                  {shopSelected.length}/5)
                </>
              }
              height={40}
              onClick={() => shopSelected.length && (setModalMutiSelect(true), form.resetFields())}
            />
          </Col>
        </Col>
      </Row>
    );
  };

  const Dashboard = () => {
    const mapData = [
      {
        type: "ส่วนลดรวม",
        value: dataSummary?.totalDiscount || 0,
        color: "#0068F4",
        bgColor: "#EEF6FE",
      },
      {
        type: "ใช้ไป",
        value: dataSummary?.used || 0,
        color: "#F24040",
        bgColor: "#FEECEC",
      },
      { type: "คงเหลือ", value: dataSummary?.remaining || 0, color: "#008236", bgColor: "#F0FDF4" },
    ];

    return (
      <>
        <Row justify={"space-between"} gutter={8}>
          {mapData.map((item, index) => (
            <Col span={8} key={index}>
              <Card
                style={{
                  backgroundColor: item.bgColor,
                  borderColor: item.bgColor,
                  borderRadius: 5,
                }}
              >
                <span
                  className='card-label font-weight-bolder'
                  style={{
                    fontSize: 30,
                    fontWeight: "bold",
                    color: item.color,
                    borderRadius: 5,
                  }}
                >
                  {item.type === "ใช้ไป"
                    ? `-${numberFormatter(item.value)}`
                    : numberFormatter(item.value)}{" "}
                  ฿
                </span>
                <br />
                <Text style={{ fontSize: 16 }}>{item.type}</Text>{" "}
                {index == 0 && (
                  <Popover
                    placement='bottom'
                    content={
                      <Text level={6} color='white'>
                        เฉพาะร้านที่เปิดใช้งาน
                      </Text>
                    }
                    color={color.BK}
                  >
                    <ExclamationCircleOutlined />
                  </Popover>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const selectedOne = (e: boolean, cusComId: string) => {
    const newData = data.data.map((item: any) =>
      item.customerCompanyId === cusComId ? { ...item, isChecked: e } : item,
    );
    setData({ ...data, data: newData });
    setShopSelected((prev) => {
      if (!e) {
        return prev.filter((item) => item.customerCompanyId !== cusComId);
      }
      const selectedItem = newData.find((item) => item.customerCompanyId === cusComId);
      if (!selectedItem) {
        return prev;
      }

      return Array.from(
        new Map([...prev, selectedItem].map((item) => [item.customerCompanyId, item])).values(),
      );
    });
  };

  const resetSelected = () => {
    const mapData = data.data.map((d: any) => {
      return {
        ...d,
        isChecked: false,
      };
    });
    setData({ ...data, data: mapData });
    setShopSelected([]);
  };

  const exportMutiExcel = async () => {
    try {
      await form.validateFields().then(async (values) => {
        setIsExporting(true);
        setModalMutiSelect(false);
        const { startDate, endDate, documentType } = form.getFieldsValue(true);
        const cusIdList = shopSelected.map((p) => p.customerCompanyId);
        const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
        const endDateStr = dayjs(endDate).format("YYYY-MM-DD");
        const params = {
          customerCompanyIds: cusIdList,
          startDate: startDateStr,
          endDate: endDateStr,
        };
        let blob: Blob = new Blob([], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        try {
          if (documentType === "CO") {
            const config = await excelMutiCo(params);
            if (config) {
              blob = new Blob([config], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
            }
          } else {
            const config = await excelMutiCoAccounting(params);
            if (config) {
              blob = new Blob([config], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
            }
          }
          startUploadProcess(
            "",
            progressValue,
            setProgressValue,
            {
              placement: "bottomRight",
              duration: 2,
              label: "กำลังเตรียมไฟล์ของคุณ รอสักครู่!",
              progressInterval: 900,
            },
            async () => {
              try {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `${documentType === "CO" ? "co" : "co-accounting"}-${new Date()
                  .toLocaleDateString("us-EN", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                  .replace(/\//g, "-")}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(a.href);
                // formatFileSize(blob.size);
              } catch (error) {
                message.error("Export Excel ไม่สำเร็จ");
              } finally {
                setModalMutiSelect(false);
                setIsExporting(false);
              }
            },
          );
        } catch (error) {
          console.error("Error submitting report:", error);
        } finally {
          resetSelected();
          setModalMutiSelect(false);
        }
      });
    } catch (e: any) {
      console.log(e);
    }
  };

  const setParams = (params?: { searchText?: string; searchZone?: string; page?: string }) => {
    const nextSearchText = params?.searchText ?? searchText;
    const nextSearchZone = params?.searchZone ?? searchZone;
    const nextPage = params?.page ?? page;

    setSearchParams({
      searchText: nextSearchText || "",
      searchZone: nextSearchZone || "",
      page: nextPage,
    });
  };

  const columns: any = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: "4%",
      align: "center",
      hidden: !isExcel,
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol align='center'>
              <Checkbox
                checked={row.isChecked}
                onChange={(e) => selectedOne(e.target.checked, row.customerCompanyId)}
                disabled={shopSelected.length === 5 && !row.isChecked}
              />
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='ชื่อร้านค้า'
          sortDirection={
            orderTabState.sortField === "customerName" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("customerName")}
        />
      ),

      dataIndex: "customerName",
      key: "customerName",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text level={6} color='Text3'>
                {row?.customerNo} • เขต {row?.zone}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SortFieldCustom
            title='จำนวนส่วนลด'
            sortDirection={
              orderTabState.sortField === "coCount" ? orderTabState.sortDirection : undefined
            }
            onSortChange={() => orderTabState.handleSortChange("coCount")}
          />
        </div>
      ),
      dataIndex: "coCount",
      key: "coCount",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "center" }}>
              <Text>{value}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SortFieldCustom
            title='ส่วนลดรวม'
            sortDirection={
              orderTabState.sortField === "totalDiscount" ? orderTabState.sortDirection : undefined
            }
            onSortChange={() => orderTabState.handleSortChange("totalDiscount")}
          />
        </div>
      ),
      dataIndex: "totalDiscount",
      key: "totalDiscount",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "flex-end" }}>
              <Text>{numberFormatter(value)} ฿</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SortFieldCustom
            title='ใช้ไป'
            sortDirection={
              orderTabState.sortField === "used" ? orderTabState.sortDirection : undefined
            }
            onSortChange={() => orderTabState.handleSortChange("used")}
          />
        </div>
      ),
      dataIndex: "used",
      key: "used",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "flex-end" }}>
              <Text color={value > 0 ? "error" : "Text1"}>
                {value > 0 ? `-${numberFormatter(value)}` : numberFormatter(value)} ฿
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SortFieldCustom
            title='คงเหลือ'
            sortDirection={
              orderTabState.sortField === "remaining" ? orderTabState.sortDirection : undefined
            }
            onSortChange={() => orderTabState.handleSortChange("remaining")}
          />
        </div>
      ),
      dataIndex: "remaining",
      key: "remaining",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "flex-end" }}>
              <Text color={value > 0 ? "EmeraldGreen" : "Text1"}>{numberFormatter(value)} ฿</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "จัดการ",
      dataIndex: "action",
      key: "action",
      align: "center",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <Row justify={"center"} gutter={8}>
              <Col>
                <Permission permission={["coDiscountEachShop", "view"]}>
                  <ActionBtn
                    onClick={() =>
                      navigate(
                        "/newDiscountCo/detailCoDiscountEachShop/" +
                          row.customerCompanyId +
                          "/coEachShop",
                      )
                    }
                    icon={<UnorderedListOutlined />}
                  />
                </Permission>
              </Col>
            </Row>
          ),
        };
      },
    },
  ];

  return (
    <>
      <CardContainer>
        <PageTitle />
        <br />
        <Dashboard />
        <br />
        <Row justify={"start"} gutter={8}>
          <Col span={10}>
            <Input
              value={searchText}
              allowClear
              placeholder='ค้นหาร้านค้า'
              suffix={<SearchOutlined />}
              onPressEnter={(e) => {
                setPage("1");
                setSearchText(e.currentTarget.value);
                setParams({ searchText: e.currentTarget.value, page: "1" });
              }}
              onChange={(e) => {
                if (!e.target.value) {
                  setSearchText(undefined);
                  setParams({ searchText: "", page: "1" });
                }
              }}
            />
          </Col>
          <Col span={3}>
            <Select
              value={searchZone}
              data={
                zoneList.map((z) => ({ label: z.zoneName, value: z.zoneName, key: z.zoneName })) ||
                []
              }
              placeholder='เขต'
              onChange={(value) => {
                setPage("1");
                setSearchZone(value);
                setParams({ searchZone: value, page: "1" });
              }}
            />
          </Col>
        </Row>
        <br />
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#F3F6F9",
                fontFamily: "IBM Plex Sans Thai",
                fontSize: 15,
              },
            },
          }}
        >
          <Table
            loading={loading}
            columns={columns}
            dataSource={data.data || []}
            pagination={{
              pageSize: 10,
              current: Number(page),
              total: data.total || 0,
              showSizeChanger: false,
              position: ["bottomCenter"],
              onChange: (page) => {
                setPage(String(page));
                setParams({ page: String(page) });
              },
            }}
          />
        </ConfigProvider>
      </CardContainer>
      {isExporting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(128, 128, 128, 0.18)",
            cursor: "not-allowed",
            zIndex: 999,
          }}
        />
      )}
      {modalMutiSelect && (
        <Modal
          open={modalMutiSelect}
          footer={false}
          width={1000}
          onCancel={() => setModalMutiSelect(false)}
          centered
        >
          <Text level={4} fontWeight={600}>
            ร้านค้าที่ต้องการ Export จำนวน {shopSelected.length} รายการ
          </Text>
          <Divider style={{ margin: "12px 0px 0px 12px", paddingBottom: "15px" }} />
          <Row justify='end' gutter={8} style={{ paddingBottom: "10px" }}>
            <Col>
              <Popconfirm
                placement='top'
                title={
                  <Row justify={"space-between"} gutter={8}>
                    <Col>
                      <Button
                        size='small'
                        title={
                          <Text level={6} color='primary'>
                            ยกเลิก
                          </Text>
                        }
                        typeButton='primary-light'
                        height={25}
                        style={{ padding: "0 12px" }}
                        onClick={() => resetSelected()}
                      />
                    </Col>
                    <Col>
                      <Button
                        size='small'
                        title={
                          <Text level={6} color='white'>
                            ลบ
                          </Text>
                        }
                        typeButton='danger'
                        height={25}
                        style={{ padding: "0 12px" }}
                        onClick={() => {
                          setModalMutiSelect(false);
                          resetSelected();
                        }}
                      />
                    </Col>
                  </Row>
                }
                icon={false}
                showCancel={false}
                okButtonProps={{ style: { display: "none" } }}
              >
                <Text level={6} color='primary' style={{ cursor: "pointer" }}>
                  ล้างทั้งหมด
                </Text>
              </Popconfirm>
            </Col>
          </Row>
          <Row
            justify='start'
            gutter={8}
            style={{
              padding: "8px",
              backgroundColor: color.Disable,
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
            }}
          >
            <Col span={4}>
              <Text fontWeight={600}> Customer No</Text>
            </Col>
            <Col span={15}>
              <Text fontWeight={600}>ชื่อร้านค้า</Text>
            </Col>
            <Col span={3}>
              <Text fontWeight={600}>เขต</Text>
            </Col>
            <Col span={2} style={{ textAlign: "right" }}>
              <Text fontWeight={600}>จัดการ</Text>
            </Col>
          </Row>
          <div style={{ maxHeight: 400, overflowY: "auto", paddingRight: 8, paddingTop: 8 }}>
            {shopSelected.map((d: any, i: number) => (
              <Row key={i} justify='start' gutter={16} style={{ padding: "8px" }}>
                <Col span={4}>
                  <Text>{d.customerNo}</Text>
                </Col>
                <Col span={15}>
                  <Text>{d.customerName}</Text>
                </Col>
                <Col span={3}>
                  <Text>{d.zone}</Text>
                </Col>
                <Col span={2} style={{ textAlign: "right" }}>
                  <Popconfirm
                    placement='top'
                    open={deleteConfirmId === d.customerCompanyId}
                    onOpenChange={(open) => setDeleteConfirmId(open ? d.customerCompanyId : null)}
                    title={
                      <Row justify={"space-between"} gutter={8}>
                        <Col>
                          <Button
                            size='small'
                            title={
                              <Text level={6} color='primary'>
                                ยกเลิก
                              </Text>
                            }
                            typeButton='primary-light'
                            height={25}
                            style={{ padding: "0 12px" }}
                            onClick={() => setDeleteConfirmId(null)}
                          />
                        </Col>
                        <Col>
                          <Button
                            size='small'
                            title={
                              <Text level={6} color='white'>
                                ลบ
                              </Text>
                            }
                            typeButton='danger'
                            height={25}
                            style={{ padding: "0 12px" }}
                            onClick={() => {
                              selectedOne(false, d.customerCompanyId);
                              setDeleteConfirmId(null);
                              if (shopSelected.length === 1) {
                                setModalMutiSelect(false);
                              }
                            }}
                          />
                        </Col>
                      </Row>
                    }
                    icon={false}
                    showCancel={false}
                    okButtonProps={{ style: { display: "none" } }}
                  >
                    <ActionBtn
                      icon={
                        <DeleteOutlined
                          style={{ color: color.error }}
                          //onClick={() => selectedOne(false, d.customerCompanyId)}
                        />
                      }
                    />
                  </Popconfirm>
                </Col>
              </Row>
            ))}
          </div>
          <Form form={form} layout='vertical'>
            {/* {profile.company !== "ICPL" && ( */}
            <Row justify={"start"} gutter={8} style={{ padding: "5px" }}>
              <Col span={24}>
                <Form.Item
                  name='documentType'
                  label='ประเภทเอกสาร'
                  rules={[
                    {
                      required: profile.company !== "ICPL" ? true : false,
                      message: "*โปรดเลือกประเภทเอกสาร",
                    },
                  ]}
                >
                  <Select
                    data={[
                      { label: "แบบรายการ CO", value: "CO", key: "CO" },
                      { label: "แบบบัญชี", value: "ACCOUNT", key: "ACCOUNT" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            {/* )} */}
            <Row justify={"space-around"} gutter={8} style={{ padding: "5px" }}>
              <Col span={12}>
                <Form.Item
                  name='startDate'
                  label='วันที่เริ่มต้น'
                  rules={[
                    {
                      required: true,
                      message: "*โปรดเลือกวันที่เริ่มต้น",
                    },
                  ]}
                >
                  <DatePicker
                    suffixIcon={<CalendarOutlined />}
                    disabledDate={(current) =>
                      current &&
                      (current.isBefore(dayjs().subtract(3, "year").startOf("year")) ||
                        current.isAfter(
                          endDateWatch ? dayjs(endDateWatch).endOf("day") : dayjs().endOf("day"),
                        ))
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='endDate'
                  label='วันที่สิ้นสุด'
                  rules={[
                    {
                      required: true,
                      message: "*โปรดเลือกวันที่สิ้นสุด",
                    },
                  ]}
                >
                  <DatePicker
                    suffixIcon={<CalendarOutlined />}
                    disabledDate={(current) =>
                      current &&
                      (current.isBefore(
                        startDateWatch
                          ? dayjs(startDateWatch).startOf("day")
                          : dayjs().subtract(3, "year").startOf("year"),
                      ) ||
                        current.isAfter(dayjs().endOf("day")))
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Alert
            message='หมายเหตุ สามารถเลือกช่วงวันที่ย้อนหลังได้ไม่เกิน 3 ปี'
            type='info'
            showIcon
          />
          <Divider />
          <Row gutter={16} justify={"end"}>
            <Col span={4}>
              <Button
                size='small'
                title='ยกเลิก'
                typeButton='primary-light'
                style={{
                  width: "100%",
                  height: "32px",
                  lineHeight: "32px",
                  padding: "0 16px",
                }}
                onClick={() => setModalMutiSelect(false)}
              />
            </Col>
            <Col span={4}>
              <Button
                size='small'
                title='Export'
                typeButton={"primary"}
                style={{
                  width: "100%",
                  height: "32px",
                  lineHeight: "32px",
                  padding: "0 16px",
                }}
                onClick={async () => {
                  exportMutiExcel();
                }}
              />
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};
